import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Award, Gift, Clock, Star, Ticket, Copy, Check, Loader2, TrendingUp, History, ShoppingBag } from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

export default function Points() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pointsData, setPointsData] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [myDiscounts, setMyDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [activeTab, setActiveTab] = useState('rewards');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pointsRes, rewardsRes, discountsRes] = await Promise.all([
        api.get('/points/me'),
        api.get('/points/rewards'),
        api.get('/points/my-discounts')
      ]);
      setPointsData(pointsRes.data);
      setRewards(rewardsRes.data);
      setMyDiscounts(discountsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, navigate, loadData]);

  const handleRedeem = async () => {
    if (!selectedReward) return;
    setRedeemLoading(true);
    try {
      const response = await api.post('/points/redeem/' + selectedReward.id);
      toast.success('Código gerado com sucesso!');
      setDialogOpen(false);
      setSelectedReward(null);
      loadData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao resgatar recompensa'));
    } finally {
      setRedeemLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Código copiado!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isExpired = (dateStr) => new Date(dateStr) < new Date();

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header with Points */}
        <div className="relative mb-8 p-8 rounded-2xl bg-gradient-to-r from-ruby/20 via-ruby/10 to-transparent border border-ruby/30 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-ruby/10 rounded-full blur-3xl" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Award className="w-8 h-8 text-ruby" />
                <h1 className="text-3xl font-bold text-foreground">Os Meus Pontos</h1>
              </div>
              <p className="text-muted-foreground">Ganhe pontos a ler e troque por descontos na Pimenta Doce</p>
            </div>
            <div className="text-center md:text-right">
              <div className="text-5xl font-bold text-ruby mb-1" data-testid="total-points">
                {pointsData?.total_points || 0}
              </div>
              <p className="text-sm text-muted-foreground">pontos disponíveis</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-surface/50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm">Total Ganhos</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{pointsData?.lifetime_points || 0}</p>
            </div>
            <div className="bg-surface/50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Gift className="w-4 h-4" />
                <span className="text-sm">Resgatados</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{pointsData?.redeemed_points || 0}</p>
            </div>
            <div className="bg-surface/50 rounded-lg p-4 col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Ticket className="w-4 h-4" />
                <span className="text-sm">Códigos Ativos</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{myDiscounts.filter(d => !d.is_used && !isExpired(d.expires_at)).length}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button variant={activeTab === 'rewards' ? 'default' : 'outline'} onClick={() => setActiveTab('rewards')} className={activeTab === 'rewards' ? 'bg-ruby' : 'border-border'}>
            <Gift className="w-4 h-4 mr-2" />
            Recompensas
          </Button>
          <Button variant={activeTab === 'discounts' ? 'default' : 'outline'} onClick={() => setActiveTab('discounts')} className={activeTab === 'discounts' ? 'bg-ruby' : 'border-border'}>
            <Ticket className="w-4 h-4 mr-2" />
            Os Meus Códigos
          </Button>
          <Button variant={activeTab === 'history' ? 'default' : 'outline'} onClick={() => setActiveTab('history')} className={activeTab === 'history' ? 'bg-ruby' : 'border-border'}>
            <History className="w-4 h-4 mr-2" />
            Histórico
          </Button>
        </div>

        {/* Rewards Tab */}
        {activeTab === 'rewards' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {rewards.map((reward) => {
              const canRedeem = pointsData?.total_points >= reward.points_required;
              const progress = Math.min((pointsData?.total_points / reward.points_required) * 100, 100);
              return (
                <Card key={reward.id} className={'bg-surface border-border ' + (canRedeem ? 'border-ruby/50' : '')} data-testid={'reward-' + reward.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <Badge className={'text-xs ' + (canRedeem ? 'bg-ruby' : 'bg-white/10')}>{reward.discount_percent}% OFF</Badge>
                      <ShoppingBag className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <CardTitle className="text-lg text-foreground">{reward.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">{reward.description}</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Progresso</span>
                        <span className="text-foreground font-medium">{pointsData?.total_points || 0} / {reward.points_required}</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    <Button className={'w-full mt-4 ' + (canRedeem ? 'bg-ruby hover:bg-ruby-light' : '')} disabled={!canRedeem} onClick={() => { setSelectedReward(reward); setDialogOpen(true); }} data-testid={'redeem-btn-' + reward.id}>
                      {canRedeem ? 'Resgatar' : `Faltam ${reward.points_required - (pointsData?.total_points || 0)} pontos`}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
            {rewards.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nenhuma recompensa disponível</p>
              </div>
            )}
          </div>
        )}

        {/* My Discounts Tab */}
        {activeTab === 'discounts' && (
          <div className="space-y-4">
            {myDiscounts.length > 0 ? myDiscounts.map((discount) => {
              const expired = isExpired(discount.expires_at);
              return (
                <Card key={discount.id} className={'bg-surface border-border ' + (expired || discount.is_used ? 'opacity-60' : '')} data-testid={'discount-' + discount.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={'p-3 rounded-lg ' + (expired || discount.is_used ? 'bg-white/5' : 'bg-ruby/20')}>
                        <Ticket className={'w-6 h-6 ' + (expired || discount.is_used ? 'text-muted-foreground' : 'text-ruby')} />
                      </div>
                      <div>
                        <h3 className="font-medium text-foreground">{discount.reward_name}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="font-mono bg-white/10 px-2 py-0.5 rounded">{discount.discount_code}</span>
                          <span>•</span>
                          <span>{discount.discount_percent}% desconto</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {expired ? 'Expirado' : discount.is_used ? 'Utilizado' : 'Válido até ' + formatDate(discount.expires_at)}
                        </p>
                      </div>
                    </div>
                    {!expired && !discount.is_used && (
                      <Button variant="outline" size="sm" onClick={() => copyCode(discount.discount_code)} className="border-border">
                        {copiedCode === discount.discount_code ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            }) : (
              <div className="text-center py-12">
                <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Ainda não resgatou nenhum código</p>
                <Button className="mt-4 bg-ruby hover:bg-ruby-light" onClick={() => setActiveTab('rewards')}>
                  Ver Recompensas
                </Button>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <Card className="bg-surface border-border">
            <CardContent className="p-0">
              {pointsData?.history?.length > 0 ? (
                <div className="divide-y divide-border">
                  {pointsData.history.map((entry) => (
                    <div key={entry.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={'p-2 rounded-lg ' + (entry.points > 0 ? 'bg-green-500/20' : 'bg-ruby/20')}>
                          {entry.points > 0 ? <Star className="w-4 h-4 text-green-400" /> : <Gift className="w-4 h-4 text-ruby" />}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{entry.action_name}</p>
                          <p className="text-sm text-muted-foreground">{entry.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={'font-bold ' + (entry.points > 0 ? 'text-green-400' : 'text-ruby')}>
                          {entry.points > 0 ? '+' : ''}{entry.points}
                        </p>
                        <p className="text-xs text-muted-foreground">{formatDate(entry.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Nenhuma atividade registada</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* How to Earn Points */}
        <Card className="bg-surface border-border mt-8">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Star className="w-5 h-5 text-ruby" />
              Como Ganhar Pontos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <div className="text-2xl font-bold text-ruby mb-1">+10</div>
                <p className="text-sm text-muted-foreground">Login diário</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <div className="text-2xl font-bold text-ruby mb-1">+5</div>
                <p className="text-sm text-muted-foreground">Ler um conto</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <div className="text-2xl font-bold text-ruby mb-1">+3</div>
                <p className="text-sm text-muted-foreground">Ler um artigo</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <div className="text-2xl font-bold text-ruby mb-1">+5</div>
                <p className="text-sm text-muted-foreground">Partilhar conteúdo</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Redeem Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-surface border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground">Confirmar Resgate</DialogTitle>
            </DialogHeader>
            {selectedReward && (
              <div className="py-4">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-ruby/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-8 h-8 text-ruby" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">{selectedReward.name}</h3>
                  <p className="text-muted-foreground mt-2">{selectedReward.description}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Desconto</span>
                    <span className="text-foreground font-medium">{selectedReward.discount_percent}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Custo</span>
                    <span className="text-ruby font-medium">{selectedReward.points_required} pontos</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Saldo após resgate</span>
                    <span className="text-foreground font-medium">{(pointsData?.total_points || 0) - selectedReward.points_required} pontos</span>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancelar</Button>
              <Button onClick={handleRedeem} disabled={redeemLoading} className="bg-ruby hover:bg-ruby-light" data-testid="confirm-redeem-btn">
                {redeemLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
                Confirmar Resgate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
